/* Shared shell interactions resolve the current section DOM on every event. */
'use strict';
(() => {
  // Theme editor replacements keep the asset loaded; never register global listeners twice.
  if (window.cnShellInitialized) return;
  window.cnShellInitialized = true;
  const dialogTriggers = new WeakMap();
  const controlled = button => document.getElementById(button?.getAttribute('aria-controls'));
  const closeShop = (root = document) => {
    root.querySelectorAll('.nav-shop-toggle').forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      const menu = controlled(button);
      if (menu) menu.hidden = true;
    });
  };
  const closeMobile = (root = document) => {
    root.querySelectorAll('.menu-button').forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', 'Open menu');
      const menu = controlled(button);
      if (menu) menu.hidden = true;
    });
  };
  const closeSection = root => {
    closeShop(root);
    closeMobile(root);
    root.querySelectorAll('dialog').forEach(dialog => {
      // Do not restore focus to a trigger that is being removed by the editor.
      dialogTriggers.delete(dialog);
      if (dialog.open) dialog.close();
    });
  };
  document.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const shopToggle = target.closest('.nav-shop-toggle');
    if (shopToggle) {
      const menu = controlled(shopToggle);
      if (menu) {
        const open = shopToggle.getAttribute('aria-expanded') !== 'true';
        shopToggle.setAttribute('aria-expanded', String(open));
        menu.hidden = !open;
      }
    } else if (!target.closest('.nav-shop') || target.closest('.nav-shop-menu a')) {
      closeShop();
    }
    const menuButton = target.closest('.menu-button');
    if (menuButton) {
      const menu = controlled(menuButton);
      if (menu) {
        const open = menuButton.getAttribute('aria-expanded') !== 'true';
        menuButton.setAttribute('aria-expanded', String(open));
        menuButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        menu.hidden = !open;
      }
    } else if (target.closest('.mobile-nav a')) {
      closeMobile();
    }
    const opener = target.closest('[data-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.open);
      if (!(dialog instanceof HTMLDialogElement)) return;
      event.preventDefault();
      dialogTriggers.set(dialog, opener);
      if (!dialog.open) dialog.showModal();
      if (dialog.id === 'search-dialog') dialog.querySelector('#search-input')?.focus();
    }
    const closer = target.closest('[data-close]');
    if (closer) closer.closest('dialog')?.close();
    if (target instanceof HTMLDialogElement) {
      const box = target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) target.close();
    }
  });
  // close does not bubble, so capture also handles dialogs inserted by section reloads.
  document.addEventListener('close', event => {
    const trigger = dialogTriggers.get(event.target);
    dialogTriggers.delete(event.target);
    if (trigger?.isConnected) trigger.focus();
  }, true);
  document.addEventListener('focusin', event => {
    document.querySelectorAll('.nav-shop-toggle[aria-expanded="true"]').forEach(button => {
      if (!button.closest('.nav-shop')?.contains(event.target)) closeShop();
    });
    document.querySelectorAll('.menu-button[aria-expanded="true"]').forEach(button => {
      const menu = controlled(button);
      if (event.target === button || menu?.contains(event.target)) return;
      closeMobile();
      // The browser may have scrolled the next link beneath the expanded sticky
      // header before focusin. Reposition it after the menu has collapsed.
      if (event.target instanceof HTMLElement && event.target.closest('main')) {
        requestAnimationFrame(() => {
          if (document.activeElement === event.target) event.target.scrollIntoView({ block: 'center', behavior: 'instant' });
        });
      }
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const shopToggle = document.querySelector('.nav-shop-toggle[aria-expanded="true"]');
    if (shopToggle) {
      closeShop();
      shopToggle.focus();
    }
    const menuButton = document.querySelector('.menu-button[aria-expanded="true"]');
    if (menuButton) {
      closeMobile();
      menuButton.focus();
    }
  });
  matchMedia('(min-width: 1181px)').addEventListener('change', event => {
    if (!event.matches) {
      if (document.activeElement?.closest('.desktop-nav')) document.querySelector('.menu-button')?.focus();
      return;
    }
    const focusedMenu = document.activeElement?.closest('.mobile-nav');
    closeMobile();
    if (focusedMenu) document.querySelector('.nav-shop-toggle')?.focus();
  });
  document.addEventListener('shopify:section:load', event => {
    closeShop(event.target);
    closeMobile(event.target);
  });
  document.addEventListener('shopify:section:unload', event => closeSection(event.target));
})();
